import { useIntl } from '@weenat/wintl';
import differenceBy from 'lodash-es/differenceBy';
import isNil from 'lodash-es/isNil';
import { useClient, useMutation, useQuery } from '../..';
import { canUser } from '../access';
import appPaths from '../appPaths';
import canBeAStation from './canBeAStation';
import canBeOnWeatherMap from './canBeOnWeatherMap';
const DEVICE_ACTIONS_IDS = [
    'view_alerts',
    'view_settings',
    'view_device',
    'view_history',
    'view_forecasts',
    'add_to_farm',
    'geolocation',
    'sharing_status',
    'remove',
    'open_in_new_tab'
];
const useDeviceActions = ({ deviceId, newTabHandler, onGeolocationActionPress, onRemoveDeviceSuccess, onPressAddToFarm, onChangeSharingStatus, excludedActions }) => {
    const client = useClient();
    const { t } = useIntl();
    const deviceRequester = client.devices.get(deviceId);
    const deviceRequest = useQuery(deviceRequester, { enabled: !isNil(deviceId) });
    const device = deviceRequest.data;
    // We cannot fetch the currentOrg from the application state since the weather map is based on what the user have access to and not the current org context
    // So we fetch the org owning the device and wait to see if it's defined or not
    // NOTE : for the same reason using device origin field doesn't make sense since its org based
    const orgRequester = client.orgs.get(device?.organizationId);
    const orgRequest = useQuery(orgRequester, { enabled: !isNil(device?.organizationId) });
    const org = orgRequest.data;
    const commonKeyToInvalidate = [deviceRequester.invalidationKey];
    const keysToInvalidate = [...commonKeyToInvalidate];
    const [removeFromOrg, removeFromOrgRequest] = useMutation(client.devices.delete(device?.id), {
        keysToInvalidate,
        onSuccess: onRemoveDeviceSuccess
    });
    const sensorSharingLabel = t(`models.device.actions.share_sensor.${device?.shared ? 'disable' : 'enable'}`, { capitalize: true });
    const sensorSharingIcon = device?.shared ? 'VisibilityOff' : 'VisibilityOn';
    const groups = [
        {
            id: 'access',
            actions: []
        },
        {
            id: 'edition',
            actions: []
        },
        {
            id: 'my_farm',
            actions: []
        },
        {
            id: 'local_actions',
            actions: []
        },
        {
            id: 'deletion',
            actions: []
        }
    ];
    if (!isNil(device)) {
        const canRemoveDevice = !isNil(org) && canUser('unlinkDevice', 'organizations', { organization: org });
        if (canBeOnWeatherMap(device)) {
            groups[0].actions.push({
                id: 'view_device',
                label: t('models.device.actions.view_device', { capitalize: true }),
                to: appPaths.devicesMap.deviceSummary(device.id),
                iconName: 'Preview'
            }, {
                id: 'view_history',
                label: t('actions.view_history', { capitalize: true }),
                to: appPaths.devicesMap.deviceHistory(device.id),
                iconName: 'History'
            }, {
                id: 'view_forecasts',
                label: t('actions.view_forecasts', { capitalize: true }),
                to: appPaths.devicesMap.deviceForecast(device.id),
                iconName: 'Weather'
            });
        }
        // Checking if org is defined because there is no point to send a user towards an administration he doesn't have access to
        if (!isNil(org)) {
            groups[1].actions.push({
                id: 'view_settings',
                label: t('titles.settings', { capitalize: true }),
                to: appPaths.admin.deviceSettings(org.id, device.id),
                iconName: 'SettingsCog'
            });
        }
        groups[1].actions.push({
            id: 'view_alerts',
            label: t('models.alert.model.name', { capitalize: true }),
            to: appPaths.devicesMap.deviceAlerts(device.id),
            iconName: 'EditNotifications'
        });
        if (canBeAStation(device) && !isNil(onPressAddToFarm)) {
            groups[2].actions.push({
                id: 'add_to_farm',
                label: t('models.device.actions.add_to_my_farm', { capitalize: true }),
                onPress: onPressAddToFarm,
                iconName: 'Farm'
            });
        }
        if (!isNil(onGeolocationActionPress)) {
            groups[3].actions.push({
                id: 'geolocation',
                label: t(!isNil(device.location)
                    ? 'models.device.actions.locate_short'
                    : 'errors.no_gps_coordinates_found'),
                onPress: onGeolocationActionPress,
                iconName: 'LocationPin',
                isDisabled: isNil(device.location)
            });
        }
        if (!isNil(org) && !isNil(newTabHandler)) {
            groups[3].actions.push({
                id: 'open_in_new_tab',
                label: t('actions.open_in_new_tab'),
                onPress: newTabHandler,
                iconName: 'OpenInNew'
            });
        }
        if (!isNil(onChangeSharingStatus)) {
            groups[3].actions.push({
                id: 'sharing_status',
                label: sensorSharingLabel,
                onPress: () => onChangeSharingStatus(!device.shared),
                iconName: sensorSharingIcon
            });
        }
        if (canRemoveDevice) {
            groups[4].actions.push({
                id: 'remove',
                label: t('models.device.actions.remove_from_org', { capitalize: true }),
                onPress: async () => {
                    await removeFromOrg();
                },
                state: removeFromOrgRequest,
                iconName: 'Bin',
                isDanger: true,
                needConfirmation: true
            });
        }
    }
    if (!isNil(excludedActions)) {
        const excludedActionsIds = [];
        for (const excludedAction of excludedActions.values()) {
            excludedActionsIds.push({ id: excludedAction });
        }
        for (const group of groups) {
            group.actions = differenceBy(group.actions, excludedActionsIds, 'id');
        }
    }
    return groups;
};
export default useDeviceActions;
